// Schreiben-Assistent (#586): Platzhalter der Vorlage als Eingabefelder, Füllen je Empfänger, fehlende Werte,
// Anlagenverzeichnis. {{gruppe.feld}} kommt aus den Empfängerdaten, {{eingabe.schluessel|Beschriftung|typ}} aus der
// Eingabeleiste (für alle Empfänger gleich). Alte Lücken „[bitte … eintragen]“ werden zu Eingabe-Platzhaltern.
// Etappe 5: bedingte Abschnitte und Berechnungen (bausteine.ts) werden vor den einfachen Platzhaltern aufgelöst.
// #592: {{eingabe.k|Beschriftung|typ|gruppe.feld}} – Vorbelegung aus den Empfängerdaten (oder ein fester Wert).
// Eine Eingabe darf selbst Daten-Platzhalter enthalten ({{eigentuemer.anschrift}}) – die werden je Empfänger gefüllt.
import { abschnittFehler, loeseAbschnitte, rechne, type WertQuelle } from './bausteine';
import type { EingabeFeld } from './typen';

const EINGABE = /\{\{\s*eingabe\.([\w-]+)\s*(?:\|([^|}]*))?(?:\|(text|datum|betrag|janein|ankreuzen|auswahl:[^|}]+))?(?:\|([^|}]+))?\s*\}\}/g;
/** Eingaben in Bedingungen/Berechnungen: „eingabe.k“ oder „eingabe.k|Beschriftung|typ“ */
const EINGABE_INNEN = /eingabe\.(\w+)(?:\|([^|}]*)\|(text|datum|betrag|janein|ankreuzen|auswahl:[^|}=!]*[^|}=!\s]))?/g;
const BAUSTEIN = /\{\{\s*(?:#wenn|=)([^}]*)\}\}/g;
const RECHNUNG = /\{\{\s*=([^}]*)\}\}/g;
const WERT = /\{\{\s*(?!eingabe\.)([a-z_]+(?:\.[a-z_]+)+)\s*\}\}/gi;

const feldAus = (schluessel: string, beschriftung: string | undefined, typ: string | undefined, vor?: string): EingabeFeld => {
  const t = typ ?? 'text';
  const f: EingabeFeld = { schluessel, beschriftung: (beschriftung ?? '').trim() || schluessel, typ: (t.startsWith('auswahl:') ? 'auswahl' : t) as EingabeFeld['typ'] };
  if (f.typ === 'auswahl') f.optionen = t.slice(8).split('/').map((o) => o.trim()).filter(Boolean);
  if (vor?.trim()) f.vor = vor.trim();
  return f;
};

const DATENSCHLUESSEL = /^[a-z_]+(?:\.[a-z_]+)+$/i;
/** Vorbelegung als Eingabewert: Datenfeld → {{gruppe.feld}} (je Empfänger), sonst der feste Wert. */
export function vorgabeWert(f: Pick<EingabeFeld, 'vor'> | undefined): string | undefined {
  if (!f?.vor) return undefined;
  return DATENSCHLUESSEL.test(f.vor) ? `{{${f.vor.toLowerCase()}}}` : f.vor;
}

/** Eingaben samt Vorbelegung: was nicht angefasst wurde (undefined), bekommt die Vorgabe der Vorlage.
 *  #589: Ankreuzfelder sind nie „fehlend“ – leer heißt „Nein“ (nicht angekreuzt). */
export function mitVorgaben(text: string, eingaben: Record<string, string>): Record<string, string> {
  const aus = { ...eingaben };
  for (const f of eingabeFelder(text)) {
    const v = vorgabeWert(f);
    if (aus[f.schluessel] === undefined && v !== undefined) aus[f.schluessel] = v;
    if (f.typ === 'ankreuzen' && aus[f.schluessel] !== 'Ja') aus[f.schluessel] = 'Nein';
  }
  return aus;
}

/** #589: Kästchen im Brief – angekreuzt ☒, sonst ☐ (im PDF □/■, siehe pdfText) */
export const kaestchen = (wert: string | undefined) => (wert === 'Ja' ? '☒' : '☐');
const eingabeText = (m: string, k: string, typ: string | undefined, eingaben: Record<string, string>) =>
  typ === 'ankreuzen' ? kaestchen(eingaben[k]) : eingaben[k]?.trim() ? eingaben[k] : m;

/** Daten-Platzhalter in einem Eingabewert füllen; Unbekanntes bleibt stehen. */
export const datenIn = (wert: string, werte: Record<string, string>): string =>
  wert.replace(WERT, (m, k: string) => (werte[k.toLowerCase()]?.trim() ? werte[k.toLowerCase()] : m));
const LUECKE = /\[bitte\s+([^\]]*?)\s+eintragen\]/gi;

/** Alle Eingabefelder (Reihenfolge des ersten Auftretens). Lücken heißen luecke1, luecke2 … über alle Texte. */
export function eingabeFelder(...texte: string[]): EingabeFeld[] {
  const felder = new Map<string, EingabeFeld>();
  let n = 0;
  for (const t of texte) {
    const treffer: { pos: number; f: EingabeFeld; nackt?: boolean }[] = [];
    for (const m of t.matchAll(EINGABE)) treffer.push({ pos: m.index ?? 0, f: feldAus(m[1], m[2], m[3], m[4]) });
    for (const m of t.matchAll(LUECKE)) treffer.push({ pos: m.index ?? 0, f: { schluessel: '', beschriftung: m[1].trim(), typ: 'text' } });
    for (const b of t.matchAll(BAUSTEIN)) {
      for (const m of b[1].matchAll(EINGABE_INNEN)) treffer.push({ pos: (b.index ?? 0) + (m.index ?? 0), f: feldAus(m[1], m[2], m[3]), nackt: !m[3] });
    }
    for (const { f, nackt } of treffer.sort((a, b) => a.pos - b.pos)) {
      const schluessel = f.schluessel || `luecke${++n}`;
      // eine spätere Deklaration (mit Beschriftung/Typ) ersetzt einen nackten Verweis, die Reihenfolge bleibt
      const alt = felder.get(schluessel);
      if (!alt) felder.set(schluessel, { ...f, schluessel, ...(nackt ? { nackt: true } : {}) } as EingabeFeld);
      else if ((alt as EingabeFeld & { nackt?: boolean }).nackt && !nackt) felder.set(schluessel, { ...f, schluessel });
    }
  }
  return [...felder.values()].map(({ nackt: _n, ...f }: EingabeFeld & { nackt?: boolean }) => f);
}

/** „[bitte X eintragen]“ → {{eingabe.lueckeN|X|text}}; start = erste Nummer (für Betreff + Text hintereinander). */
export function normalisiere(text: string, start = 1): string {
  let n = start - 1;
  return text.replace(LUECKE, (_, b: string) => `{{eingabe.luecke${++n}|${b.trim()}|text}}`);
}

/** Anzahl Lücken in einem Text (für die Startnummer des nächsten Texts). */
export const zaehleLuecken = (text: string) => [...text.matchAll(LUECKE)].length;

const quelle = (werte: Record<string, string>, eingaben: Record<string, string>): WertQuelle =>
  (k) => {
    if (!k.startsWith('eingabe.')) return werte[k.toLowerCase()];
    const v = eingaben[k.slice(8)];
    if (v === undefined) return v;
    // #592: Eingabe mit Daten-Platzhalter – nur verwertbar, wenn alle Daten da sind
    const d = datenIn(v, werte);
    return new RegExp(WERT.source, 'i').test(d) ? '' : d;
  };

/** Abschnitte und Berechnungen auflösen; Berechnungen ohne Ergebnis bleiben stehen. */
function bausteineAufloesen(text: string, q: WertQuelle, beiFeld?: (k: string, v: string | undefined) => void, beiUnklar?: (b: string) => void): string {
  return loeseAbschnitte(text, q, beiFeld, beiUnklar).replace(RECHNUNG, (m, ausdruck: string) => {
    const r = rechne(ausdruck, q);
    return 'wert' in r ? r.wert : m;
  });
}

/** Platzhalter füllen; was keinen Wert hat, bleibt sichtbar stehen. */
export function fuelle(text: string, werte: Record<string, string>, eingabenRoh: Record<string, string>): string {
  const eingaben = mitVorgaben(text, eingabenRoh);
  return bausteineAufloesen(text, quelle(werte, eingaben))
    .replace(EINGABE, (m, k: string, _b?: string, typ?: string) => eingabeText(m, k, typ, eingaben))
    .replace(WERT, (m, k: string) => (werte[k.toLowerCase()]?.trim() ? werte[k.toLowerCase()] : m));
}

/** Was noch fehlt: Werte-Schlüssel bzw. Beschriftung der Eingabefelder, je einmal. Eingaben in genutzten
 *  Bedingungen sind Pflicht, Eingaben in weggelassenen Abschnitten nicht. */
export function fehlend(text: string, werte: Record<string, string>, eingabenRoh: Record<string, string>): string[] {
  const eingaben = mitVorgaben(text, eingabenRoh);
  const aus = new Set<string>();
  const felder = new Map(eingabeFelder(text).map((f) => [`eingabe.${f.schluessel}`, f.beschriftung]));
  const name = (k: string) => felder.get(k) ?? k;
  const q = quelle(werte, eingaben);
  const aufgeloest = bausteineAufloesen(text, q, (k, v) => { if (k.startsWith('eingabe.') && !v?.trim()) aus.add(name(k)); },
    (b) => aus.add(`Bedingung „${b.trim()}“ unverständlich`));
  for (const m of aufgeloest.matchAll(new RegExp(RECHNUNG.source, 'g'))) {
    const r = rechne(m[1], q);
    if ('fehlt' in r) for (const k of r.fehlt) aus.add(name(k));
    if ('fehler' in r) aus.add(`Berechnung „${m[1].trim()}“: ${r.fehler}`);
  }
  const abschnitt = abschnittFehler(aufgeloest);
  if (abschnitt) aus.add(abschnitt);
  const rest = aufgeloest.replace(RECHNUNG, '')
    .replace(EINGABE, (m, k: string, _b?: string, typ?: string) => eingabeText(m, k, typ, eingaben))
    .replace(WERT, (m, k: string) => (werte[k.toLowerCase()]?.trim() ? werte[k.toLowerCase()] : m));
  for (const m of rest.matchAll(new RegExp(WERT.source, 'gi'))) {
    // fuelle lässt nur Unbekanntes stehen; Eingaben gesondert mit Beschriftung
    aus.add(m[1].toLowerCase());
  }
  for (const m of rest.matchAll(new RegExp(EINGABE.source, 'g'))) aus.add((m[2] ?? '').trim() || m[1]);
  for (const m of rest.matchAll(new RegExp(LUECKE.source, 'gi'))) aus.add(m[1].trim());
  return [...aus];
}

/** „Anlagen: 1. …“ unter das Schreiben. */
export function mitAnlagenverzeichnis(text: string, anlagen: { name: string }[]): string {
  if (!anlagen.length) return text;
  return `${text.trimEnd()}\n\nAnlagen:\n${anlagen.map((a, i) => `${i + 1}. ${a.name}`).join('\n')}`;
}
